//Custom components

import { useState } from 'react';
//Services
import { useDashboardData } from '@/hooks/ReactQueryHooks/useDashboardQuery';
//Assets and miscellaneous
import type { DashboardType } from '@/types/Analytics.ts';
import { accountingFormatNumber } from '@/utils';
import Card from '../../UI/Card/index.tsx';
import { Widget } from '../constants';
import { sourceLimits } from '../FundsLeftWidget';
import WidgetLayout from '../WidgetLayout';

type SourceSpentType = {
  name: string;
  total_amount: number;
};

type projectSpendingSummaryReportReturn = {
  name: string;
  total_amount: number;
  limit: number;
  spent_percentage: string;
};

export default function SourceSpentWidget({ dashboard }: { dashboard: DashboardType }) {
  const fetchSourceSpentWidget = useDashboardData({
    dashboard,
    widget: Widget.PROJECTS_SPENDING_SUMMARY_REPORT,
  });
  const sourceSpentWidgetData = fetchSourceSpentWidget?.data?.data;

  const projectSpendingSummaryReport = Object.keys(sourceLimits).reduce((acc: projectSpendingSummaryReportReturn[], source) => {
    const limit = sourceLimits[source];
    const dbSource = sourceSpentWidgetData?.find((data: SourceSpentType) => data.name === source);

    if (!dbSource) {
      acc.push({
        name: source,
        total_amount: 0,
        limit,
        spent_percentage: '0%',
      });
    } else {
      acc.push({
        ...dbSource,
        limit,
        spent_percentage: `${((dbSource.total_amount / limit) * 100).toFixed(2)}%`,
      });
    }

    return acc;
  }, []);

  const [intl] = useState(() =>
    Intl.NumberFormat('en-US', {
      notation: 'compact',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })
  );

  return (
    <>
      {sourceSpentWidgetData && (
        <WidgetLayout dashboard={dashboard} widget={Widget.PROJECTS_SPENDING_SUMMARY_REPORT}>
          <Card title='Sources Spent' error={fetchSourceSpentWidget.isError}>
            <div className='px-5 text-center py-5'>
              <h3 className='text-gray-500 font-bold'>Fiscal Year 2023 - Program Year 48 Program Spending</h3>
            </div>
            <div className='w-full h-full'>
              <table className='divide-y divide-gray-200 w-full'>
                <thead className='bg-gray-50 border-primaryColor-normal'>
                  <tr>
                    <th className='px-6 py-4 text-left text-xs font-bold text-neutralColor-normal capitalize tracking-wider'>Source</th>
                    <th className='px-6 py-4 text-left text-xs font-bold text-neutralColor-normal capitalize tracking-wider'>Allocated</th>
                    <th className='px-6 py-4 text-left text-xs font-bold text-neutralColor-normal capitalize tracking-wider'>Spent ($)</th>
                    <th className='px-6 py-4 text-left text-xs font-bold text-neutralColor-normal capitalize tracking-wider'>Spent (%)</th>
                    <th className='px-6 py-4 text-left text-xs font-bold text-neutralColor-normal capitalize tracking-wider'>Left ($)</th>
                    <th className='px-6 py-4 text-left text-xs font-bold text-neutralColor-normal capitalize tracking-wider'>Left (%)</th>
                  </tr>
                </thead>
                <tbody className='bg-white divide-y divide-gray-200'>
                  {projectSpendingSummaryReport?.map((data: projectSpendingSummaryReportReturn, index: number) => {
                    return (
                      <tr key={index} className='hover:bg-green-50 cursor-pointer'>
                        <td className='px-6 py-4'>{data.name}</td>
                        <td className='px-6 py-4'>
                          {accountingFormatNumber({
                            num: data.limit ? data.limit : 0,
                            format: 'en-US',
                            currency: 'USD',
                          })}
                        </td>
                        <td className='px-6 py-4'>
                          {accountingFormatNumber({
                            num: data.total_amount ? data.total_amount : 0,
                            format: 'en-US',
                            currency: 'USD',
                          })}
                        </td>
                        <td className='px-6 py-4'>{data.spent_percentage}</td>
                        <td className='px-6 py-4'>
                          {accountingFormatNumber({
                            num: data.total_amount ? data.limit - data.total_amount : 0,
                            format: 'en-US',
                            currency: 'USD',
                          })}
                        </td>
                        <td className='px-6 py-4'>{`${intl.format(100 - parseFloat(data.spent_percentage))}%`}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </WidgetLayout>
      )}
    </>
  );
}
